/**
 * Tax display helpers.
 *
 * Shared, presentation-only logic for summarizing an order's tax lines into a
 * concise effective rate and a "source" descriptor (auto scheme, pinned scheme,
 * manual, or exempt). Used by the Subtotals Tax line and the Tax drawer so both
 * describe the applied tax identically.
 *
 * An order's `tax_lines` array holds two kinds of row: tax, and fees (delivery,
 * documentation, handling). They share a structure but never a total, so every
 * function here starts by splitting them. Summing the array unfiltered — which
 * this file used to do — folds a delivery charge into the displayed tax rate.
 *
 * These summaries are intentionally UI-only — they are never rendered onto the
 * printed/emailed order, which shows only the scalar tax amount.
 */

import type { OrderTaxLine } from "@/types/sales-order";

/** Visual tone for the tax source chip. */
export type TaxSourceTone = "auto" | "scheme" | "manual" | "exempt";

export interface TaxSource {
  /** Short label, e.g. a scheme name, "Manual", "Exempt", or "Auto". */
  label: string;
  tone: TaxSourceTone;
}

/** Tailwind classes for the tax source chip, keyed by tone. */
export const TAX_CHIP_CLASS: Record<TaxSourceTone, string> = {
  auto: "text-[var(--text-tertiary)] bg-[var(--surface-sunken)]",
  scheme:
    "text-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/10",
  manual: "text-amber-700 dark:text-amber-300 bg-amber-500/10",
  exempt: "text-[var(--text-secondary)] bg-[var(--surface-sunken)]",
};

export interface ChargeRowSplit {
  tax: OrderTaxLine[];
  fee: OrderTaxLine[];
  taxTotal: number;
  feeTotal: number;
}

/**
 * Split an order's tax lines into the Tax rows and the Fee rows.
 *
 * The one place that decides which bucket a row belongs to, mirroring
 * `sum_tax_lines` on the backend. Every consumer should route through it rather
 * than filtering inline, because getting it wrong is invisible: the numbers still
 * render, they are just the wrong ones.
 *
 * A row with no `charge_class` counts as tax. That means it predates the split,
 * and mis-filing history as fees would be worse than leaving it where it was.
 */
export function splitChargeRows(taxLines: OrderTaxLine[]): ChargeRowSplit {
  const tax: OrderTaxLine[] = [];
  const fee: OrderTaxLine[] = [];

  for (const line of taxLines) {
    if ((line.charge_class ?? "tax") === "tax") tax.push(line);
    else fee.push(line);
  }

  const sum = (rows: OrderTaxLine[]) =>
    rows.reduce((total, row) => total + Number(row.applied_tax), 0);

  return { tax, fee, taxTotal: sum(tax), feeTotal: sum(fee) };
}

/**
 * The inputs the engine uses to build an order's taxable base.
 *
 * Purchase orders have neither a discount nor taxable charges, so they pass only
 * a subtotal. Sales orders have both, and leaving them out makes any rate derived
 * here disagree with the one the engine actually applied.
 */
export interface TaxBasis {
  /** Sum of line net extended prices, before the order-level discount. */
  subtotal: number;
  discountAmount?: number;
  /** Charges flagged taxable, which join the base. */
  taxableCharges?: number;
}

/**
 * The amount tax is charged on: `subtotal - discount + taxableCharges`.
 *
 * Mirrors `compute_order_totals` in `app/utils/order_totals.py`. Floored at zero.
 */
export function taxableBase(basis: TaxBasis): number {
  const base =
    basis.subtotal - (basis.discountAmount ?? 0) + (basis.taxableCharges ?? 0);
  return Math.max(base, 0);
}

/**
 * Compute the effective tax rate as a decimal fraction (e.g. 0.0725).
 *
 * Reads tax rows only, and divides by the engine's taxable base rather than the
 * raw subtotal — on a discounted order those differ, and dividing by the subtotal
 * reports a rate nobody set.
 *
 * A single tax row reports its own declared rate, but only when that rate
 * reconciles against the amount actually applied. A `manual_amount` row carries a
 * derived display rate and a frozen dollar figure that can drift apart as the
 * order changes; in that case the blended rate is the honest answer.
 *
 * Returns null when there is no tax or the base is non-positive.
 */
export function effectiveTaxRate(
  taxLines: OrderTaxLine[],
  basis: TaxBasis,
): number | null {
  const { tax: rows, taxTotal } = splitChargeRows(taxLines);
  const base = taxableBase(basis);
  if (!rows.length || base <= 0) return null;

  const only = rows.length === 1 ? rows[0] : null;
  if (only && only.scheme_type === "percentage") {
    const declared = Number(only.rate);
    const reconciles =
      declared > 0 &&
      Math.abs(declared * base - Number(only.applied_tax)) < 0.01;
    if (reconciles) return declared;
  }

  if (taxTotal === 0) return 0;
  return taxTotal / base;
}

/** Format a decimal-fraction rate as a percentage string, e.g. "7.25%". */
export function formatRatePct(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

/**
 * Describe how the current tax was determined, for the source chip.
 *
 * Whole-order directives (manual %, manual amount, pinned scheme, exempt) produce
 * a single tax row whose `override_mode` drives the label.
 *
 * Fee rows are excluded before any of that, which is what stops an ad hoc
 * delivery charge labelling the order's tax "Overridden": those rows carry
 * `is_overridden = true` as plumbing that protects them from recalculation, not
 * because anyone overrode a tax. It also keeps the single-row cases working when
 * a fee row happens to coexist — otherwise "Exempt" silently degrades to
 * "Overridden" the moment someone adds a charge.
 *
 * Returns null when there are no tax rows.
 */
export function taxSource(taxLines: OrderTaxLine[]): TaxSource | null {
  const { tax: rows } = splitChargeRows(taxLines);
  if (!rows.length) return null;

  const only = rows.length === 1 ? rows[0] : null;
  if (only) {
    switch (only.override_mode) {
      case "exempt":
        return { label: "Exempt", tone: "exempt" };
      case "manual_percent":
      case "manual_amount":
        return { label: "Manual", tone: "manual" };
      case "forced_scheme":
        return { label: only.scheme_name, tone: "scheme" };
    }
  }

  if (rows.some((row) => row.is_overridden)) {
    return { label: "Overridden", tone: "manual" };
  }

  if (only) {
    return { label: only.scheme_name, tone: "auto" };
  }
  return { label: "Auto", tone: "auto" };
}
